import { create } from "zustand";
import { jqXHR, ResponseError } from "../types/Api";
import { DeviceOptions } from "../types/Device";
import Api from "../utils/Api";

export const devicesURL = (): string => {
  return "/ajax/devices";
};

export interface DevicesStore {
  devices: DeviceOptions[] | undefined;
  loaded: boolean;
  fetch: () => Promise<DeviceOptions[]>;
  reset: () => void;
}

/**
 * Provides a list of all devices the current user has access to.
 *
 * Usage example:
 * ```
 * export const MyPage: React.FC = () => {
 *   const [devices, devicesLoaded] = useDevicesStore(
 *     useCallback((state) => [state.devices, state.loaded] as const, [])
 *   );
 *
 *   ...
 * };
 * ```
 */
export const useDevicesStore = create<DevicesStore>((set) => ({
  devices: undefined,

  loaded: false,

  fetch: async (): Promise<DeviceOptions[]> => {
    let devices: DeviceOptions[] | undefined,
      statusCode = 200;

    // eslint-disable-next-line
    await Api.getAsync(devicesURL()).then(
      (response: { devices: DeviceOptions[] }) => (devices = response.devices),
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (!devices || statusCode !== 200) {
      throw new ResponseError("Failed to fetch user devices", statusCode);
    }

    set({ devices, loaded: true });

    return devices;
  },

  reset: () => {
    set({ devices: undefined, loaded: false });
  },
}));
